import { useEffect, useState } from 'react'
import { CheckCircle, CircleAlert, Loader2 } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, CardContent } from 'components/ui/card'
import { StepNavigation } from 'components/step-navigation'
import { Button } from 'components/ui/button'
import type { DealState } from 'submission/lib/types'
import {
  fetchDealFormData,
  formatDealStateLabel,
  submitDeal,
} from 'submission/lib/deal-api'

interface SubmissionReceivedScreenProps {
  dealId?: string | number
}

export function SubmissionReceivedScreen({
  dealId,
}: SubmissionReceivedScreenProps) {
  const navigate = useNavigate()
  const { projectId, dealId: paramDealId } = useParams<{
    projectId?: string
    dealId?: string
  }>()
  const [loadError, setLoadError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [submissionData, setSubmissionData] = useState<{
    submittedAt: string | null
    dealState?: DealState | null
  }>({
    submittedAt: null,
    dealState: null,
  })

  const resolvedDealId = dealId ?? paramDealId ?? projectId ?? null

  useEffect(() => {
    if (!resolvedDealId) {
      setLoadError('Missing deal identifier.')
      setIsLoaded(true)
      return
    }

    let isMounted = true
    const load = async () => {
      setLoadError(null)
      try {
        const data = await fetchDealFormData(String(resolvedDealId))
        if (!isMounted) return

        if (data.dealState === 'DRAFT') {
          navigate(`/deals/${resolvedDealId}/edit`)
          return
        }

        setSubmissionData({
          submittedAt: data.submittedAt ?? null,
          dealState: data.dealState ?? null,
        })
      } catch (error) {
        console.error('Failed to load submission info', error)
        if (isMounted)
          setLoadError('Unable to load the latest submission details.')
      } finally {
        if (isMounted) setIsLoaded(true)
      }
    }
    load()
    return () => {
      isMounted = false
    }
  }, [resolvedDealId, navigate])

  const submittedDate = submissionData.submittedAt
    ? new Date(submissionData.submittedAt)
    : new Date()
  const formattedDate = submittedDate.toLocaleString()
  const normalizedDealState = submissionData.dealState ?? null
  const isRequestedChange = normalizedDealState === 'REQUESTED_CHANGE'
  const isDraft = normalizedDealState === 'DRAFT'
  const statusText = (() => {
    if (normalizedDealState === 'SUBMITTED') return 'Under review'
    if (isRequestedChange) return 'Revision required'
    return formatDealStateLabel(normalizedDealState) ?? 'Draft'
  })()
  const editLabel = isRequestedChange
    ? 'Review & Edit Submission'
    : 'Edit submission'
  const hideActions =
    normalizedDealState === 'SUBMITTED' || isRequestedChange || isDraft
  const displaySubmittedAt = formattedDate
  const displayStatus = statusText

  const handleSubmit = async () => {
    if (!resolvedDealId) {
      setSubmitError('Missing deal identifier.')
      return
    }
    setSubmitError(null)
    setIsSubmitting(true)
    try {
      await submitDeal(String(resolvedDealId))
      // Refresh the latest deal data after submitting
      const data = await fetchDealFormData(String(resolvedDealId))
      setSubmissionData({
        submittedAt: data.submittedAt ?? null,
        dealState: data.dealState ?? null,
      })
    } catch (error) {
      console.error('Failed to submit deal', error)
      setSubmitError('Failed to submit. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  const steps = [
    { id: 1, title: 'Deal Overview', component: null },
    { id: 2, title: 'Project Overview', component: null },
    { id: 3, title: 'Deal Terms', component: null },
    { id: 4, title: 'Fund Allocation', component: null },
    { id: 5, title: 'Sale Timeline', component: null },
    { id: 6, title: 'Trading & Royalties', component: null },
    { id: 7, title: 'Legal Agreements', component: null },
    { id: 8, title: 'Accessory Project Services', component: null },
  ]

  const completedSteps = [1, 2, 3, 4, 5, 6, 7, 8]

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex gap-8">
        <aside className="hidden lg:block w-[220px] shrink-0">
          <div className="sticky top-8">
            <StepNavigation
              steps={steps}
              currentStep={0}
              completedSteps={completedSteps}
            />
          </div>
        </aside>

        <main className="w-[768px]">
          <div className="flex flex-col justify-center space-y-8">
            <div className="text-center space-y-4">
              {isRequestedChange ? (
                <CircleAlert className="text-amber-500 mx-auto h-11 w-11" />
              ) : (
                <CheckCircle className="text-green-500 mx-auto h-11 w-11" />
              )}
              <h1 className="text-3xl font-bold text-neutral-900">
                {isDraft
                  ? 'Draft saved'
                  : isRequestedChange
                  ? 'Submission Requires Revision'
                  : 'Submission received'}
              </h1>
            </div>

            <Card className="w-full">
              <CardContent className="p-6 space-y-4">
                {!isRequestedChange &&
                  normalizedDealState !== 'SUBMITTED' &&
                  !isDraft && (
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-neutral-600">
                        Submitted at:
                      </span>
                      <span className="font-medium text-neutral-900 text-sm">
                        {displaySubmittedAt}
                      </span>
                    </div>
                  )}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-neutral-600">Status:</span>
                  <span className="font-medium text-neutral-900 text-sm">
                    {displayStatus}
                  </span>
                </div>
                {loadError && (
                  <p className="text-sm text-status-error">{loadError}</p>
                )}
                {submitError && (
                  <p className="text-sm text-status-error">{submitError}</p>
                )}

                <hr className="border-neutral-200" />

                <div className="space-y-4">
                  <h2 className="text-xl font-bold text-neutral-900">
                    Next steps
                  </h2>
                  {isDraft ? (
                    <ul className="space-y-3 list-disc list-inside">
                      <li className="text-neutral-600 text-sm leading-relaxed">
                        Your draft has been saved successfully.
                      </li>
                      <li className="text-neutral-600 text-sm leading-relaxed">
                        You can come back and edit your submission at any time.
                      </li>
                      <li className="text-neutral-600 text-sm leading-relaxed">
                        Once you are ready, you can submit your deal for review.
                      </li>
                      <li className="text-neutral-600 text-sm leading-relaxed">
                        If you have any questions, please contact{' '}
                        <a
                          href="mailto:cs@impossible.finance"
                          className="text-brand-primary hover:text-brand-primary/80 underline"
                        >
                          cs@impossible.finance
                        </a>
                        .
                      </li>
                    </ul>
                  ) : isRequestedChange ? (
                    <div className="space-y-3 text-neutral-600 text-sm">
                      <p className="leading-relaxed">
                        Our team has reviewed your submission and identified
                        some fields that need to be updated.
                      </p>
                      <ul className="space-y-3 list-disc list-inside">
                        <li className="leading-relaxed">
                          Please review the highlighted sections
                        </li>
                        <li className="leading-relaxed">
                          Make the required changes
                        </li>
                        <li className="leading-relaxed">
                          Resubmit once completed
                        </li>
                        <li className="leading-relaxed">
                          If you have any questions, please contact{' '}
                          <a
                            href="mailto:cs@impossible.finance"
                            className="text-brand-primary hover:text-brand-primary/80 underline"
                          >
                            cs@impossible.finance
                          </a>
                          .
                        </li>
                      </ul>
                    </div>
                  ) : (
                    <ul className="space-y-3 list-disc list-inside">
                      <li className="text-neutral-600 text-sm leading-relaxed">
                        Our team will review your submission.
                      </li>
                      <li className="text-neutral-600 text-sm leading-relaxed">
                        You'll receive an email when the status is updated or if
                        additional information is needed.
                      </li>
                      <li className="text-neutral-600 text-sm leading-relaxed">
                        If you have any questions, please contact{' '}
                        <a
                          href="mailto:cs@impossible.finance"
                          className="text-brand-primary hover:text-brand-primary/80 underline"
                        >
                          cs@impossible.finance
                        </a>
                        .
                      </li>
                    </ul>
                  )}
                </div>
                {isLoaded && (
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-center pt-2">
                    {normalizedDealState !== 'SUBMITTED' && (
                      <Button
                        variant="outline"
                        disabled={!resolvedDealId}
                        className="flex h-[42px] w-full sm:w-[40%] items-center justify-center bg-transparent px-5 border-brand-primary text-brand-primary hover:border-brand-hover hover:text-brand-hover hover:bg-brand-subtleBG"
                        onClick={() =>
                          navigate(`/deals/${resolvedDealId}/edit`)
                        }
                      >
                        {editLabel}
                      </Button>
                    )}
                    {!hideActions ? (
                      <Button
                        variant="default"
                        disabled={!resolvedDealId || isSubmitting}
                        className="flex h-[42px] w-full sm:w-[40%] items-center justify-center px-5 sm:ml-4 bg-brand-primary hover:bg-brand-hover text-white disabled:bg-neutral-200 disabled:text-neutral-500"
                        onClick={handleSubmit}
                      >
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                      </Button>
                    ) : null}
                    {normalizedDealState === 'SUBMITTED' && (
                      <Button
                        variant="outline"
                        className="flex h-[42px] w-full sm:w-[40%] items-center justify-center bg-transparent px-5 border-brand-primary text-brand-primary hover:border-brand-hover hover:text-brand-hover hover:bg-brand-subtleBG"
                        onClick={() => navigate('/deals')}
                      >
                        Back to Dashboard
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    </div>
  )
}
